<template>
  <v-content>
    <v-container my-10>
      <v-layout align-center justify-center>
        <v-progress-circular
          :size="55"
          :width="4"
          indeterminate
          color="primary"
          class="mt-12"
          v-if="loading"
        />

        <v-form ref="form" v-model="valid" lazy-validation v-if="!loading">
          <v-card color="primary" dark max-width="650px" v-if="!loading" tile>
            <v-card-title>
              <span class="headline" v-if="isEditView"
                >{{ editedItem.name }}
              </span>
              <span
                class="headline"
                v-else-if="!isEditView && isInternalPartner"
                >{{ $t("users.dealersAddEdit.newRole") }}</span
              >
              <span class="headline" v-else>{{
                $t("users.dealersAddEdit.newPartner")
              }}</span>
              <v-btn v-if="canUseKnowledgeBaseBtn" @click="handleHelp" icon>
                <v-icon class="mx-2">mdi-help-circle</v-icon>
              </v-btn>
            </v-card-title>
          </v-card>

          <v-tabs
            style="width: 650px"
            fixed-tabs
            background-color="primary"
            dark
            v-model="tab"
            show-arrows
          >
            <v-tabs-slider color="white"></v-tabs-slider>
            <v-tab
              v-for="tab of tabs"
              :key="tab.id"
              :href="'#' + tab.href"
              :disabled="tab.disabled"
            >
              <v-icon class="mr-2">{{ tab.icon }}</v-icon>
              {{ tab.name }}
            </v-tab>
          </v-tabs>
          <v-tabs-items :value="tab">
            <v-tab-item value="info">
              <v-card max-width="650px" v-if="!loading">
                <v-card-text>
                  <v-container>
                    <v-row>
                      <v-col cols="12" sm="12" md="12" lg="12">
                        <v-text-field
                          v-model="editedItem.name"
                          :label="$t('users.dealersAddEdit.companyDisplayName')"
                          color="primary"
                          :rules="dealerNameRule"
                          required
                          :disabled="readonlyFields('name')"
                          autocomplete="off"
                        />
                      </v-col>
                      <v-col cols="12" sm="12" md="12" lg="12">
                        <v-text-field
                          v-model="editedItem.company_name"
                          :label="$t('users.dealersAddEdit.companyLegalName')"
                          color="primary"
                          :rules="companyRule"
                          required
                          :disabled="
                            readonlyFields('company_name') ||
                            (isInternalPartner && !isSuperGroupPartner)
                          "
                          autocomplete="off"
                        />
                      </v-col>
                      <v-col
                        cols="12"
                        sm="12"
                        md="12"
                        lg="12"
                        v-if="!isInternalPartner"
                      >
                        <brand-selector
                          class="mt-4"
                          :brand-uuid.sync="editedItem.brand_uuids"
                          :intial-data="editedItem.brand_uuids"
                          :disabled="readonlyFields('brand')"
                          :no-style="true"
                        ></brand-selector>
                        <!-- :required="!isEditView" -->
                      </v-col>
                      <v-col cols="12" sm="12" md="12" lg="12">
                        <v-text-field
                          v-model="editedItem.address"
                          :label="$t('users.dealersAddEdit.address')"
                          color="primary"
                          :disabled="
                            readonlyFields('address') ||
                            (isInternalPartner && !isSuperGroupPartner)
                          "
                          autocomplete="off"
                        />
                      </v-col>
                      <v-col cols="12" sm="12" md="12" lg="12">
                        <v-text-field
                          v-model="editedItem.city"
                          :label="$t('users.dealersAddEdit.city')"
                          color="primary"
                          :disabled="
                            readonlyFields('city') ||
                            (isInternalPartner && !isSuperGroupPartner)
                          "
                          autocomplete="off"
                        />
                      </v-col>
                      <v-col cols="12" sm="12" md="12" lg="12">
                        <country-selector
                          :initial-country="editedItem.country"
                          @change="editedItem.country = $event"
                          :disabled="
                            readonlyFields('country') ||
                            (isInternalPartner && !isSuperGroupPartner)
                          "
                        >
                        </country-selector>
                      </v-col>
                      <v-col
                        cols="12"
                        sm="12"
                        md="12"
                        lg="12"
                        v-if="
                          !(
                            editedItem.is_partner_supergroup &&
                            editedItem.is_internal_partner
                          )
                        "
                      >
                        <v-text-field
                          v-model="editedItem.email"
                          :label="$t('users.dealersAddEdit.email')"
                          color="primary"
                          :disabled="readonlyFields('email')"
                          :hint="$t('users.dealersAddEdit.visibleToCustomer')"
                          persistent-hint
                          autocomplete="off"
                        />
                      </v-col>
                      <v-col cols="12" sm="12" md="12" lg="12">
                        <v-text-field
                          v-model="editedItem.website"
                          :label="$t('users.dealersAddEdit.website')"
                          color="primary"
                          :disabled="readonlyFields('website')"
                          autocomplete="off"
                        />
                      </v-col>
                      <v-col cols="12" sm="12" md="12" lg="12">
                        <v-text-field
                          v-model="editedItem.phone"
                          :label="$t('users.dealersAddEdit.phone')"
                          color="primary"
                          :disabled="readonlyFields('phone')"
                          autocomplete="off"
                        />
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    v-if="!hideCancel"
                    color="primary"
                    text
                    @click="close"
                    >{{ $t("users.dealersAddEdit.cancel") }}</v-btn
                  >
                  <v-btn
                    color="primary"
                    text
                    @click="openCreateInitialUserDialog"
                    >{{ $t("users.dealersAddEdit.save") }}</v-btn
                  >
                </v-card-actions>
              </v-card>
            </v-tab-item>

            <v-tab-item value="subscription">
              <v-card max-width="650px" width="650px" v-if="!loading">
                <v-card-text>
                  <!-- <v-row>
                    <v-col cols="12">
                      <v-card-text class="pt-0 pb-0">
                        You are currently on the
                        <b>{{ getLicence.name }}</b> licence
                      </v-card-text>
                    </v-col>
                  </v-row> -->
                  <v-row>
                    <v-col cols="6">
                      <v-card-text class="pt-0 pb-2 font-weight-bold">
                        {{ $t("users.dealersAddEdit.premium") }}
                      </v-card-text>

                      <v-chip
                        class="mb-4 ml-2 pt-0"
                        color="primary"
                        outlined
                        :disabled="getLicence.sku !== 'basic'"
                      >
                        {{
                          getLicence.sku === "basic"
                            ? $t("users.dealersAddEdit.active")
                            : $t("users.dealersAddEdit.inactive")
                        }}
                      </v-chip>

                      <!-- <v-card-text class="pt-0 text-bold">
                        50€ per month
                      </v-card-text> -->

                      <v-card-text class="pt-0">
                        <p class="mb-0">
                          {{
                            $t(
                              "users.dealersAddEdit.groupBeneteauDeliveredBoats"
                            )
                          }}
                        </p>
                        <p class="mb-0">
                          {{
                            $t("users.dealersAddEdit.standardMaintenancePlans")
                          }}
                        </p>
                        <!-- <p class="mb-0">• Scenarios channel to sell services</p> -->
                        <p class="mb-0">
                          {{
                            $t(
                              "users.dealersAddEdit.taskAllocationToTeamMembers"
                            )
                          }}
                        </p>
                        <!-- <p class="mb-0">• Standard maintenance protocols</p> -->
                        <p class="mb-0">
                          {{ $t("users.dealersAddEdit.singleBase") }}
                        </p>
                        <p class="mb-0">
                          {{ $t("users.dealersAddEdit.unlimitedUsers") }}
                        </p>
                        <p class="mb-0">
                          {{
                            $t("users.dealersAddEdit.directMessageToCustomers")
                          }}
                        </p>
                      </v-card-text>
                    </v-col>
                    <v-col cols="6">
                      <v-card-text class="pt-0 pb-2 font-weight-bold">
                        {{ $t("users.dealersAddEdit.advanced") }}
                      </v-card-text>

                      <v-tooltip bottom :disabled="getLicence.sku !== 'basic'">
                        <template v-slot:activator="{ on, attrs }">
                          <v-chip
                            v-bind="attrs"
                            v-on="on"
                            class="mb-4 ml-2 pt-0"
                            color="primary"
                            outlined
                            :disabled="getLicence.sku !== 'advanced'"
                          >
                            <v-icon small class="mr-1">mdi-star-outline</v-icon>
                            {{
                              getLicence.sku === "advanced"
                                ? $t("users.dealersAddEdit.active")
                                : $t("users.dealersAddEdit.inactive")
                            }}
                          </v-chip>
                        </template>
                        <span
                          v-html="
                            $t(
                              'users.dealersAddEdit.toUpgradeYourLicencePleaseContact',
                              {
                                email: 'support@seanapps.fr',
                              }
                            )
                          "
                        >
                        </span>
                      </v-tooltip>
                      <!-- <v-card-text class="pt-0"> 150€ per month </v-card-text> -->

                      <v-card-text class="pt-0">
                        <p class="mb-0">
                          {{
                            $t(
                              "users.dealersAddEdit.allFeaturesInPremiumAndMultiLocationPlus"
                            )
                          }}
                        </p>
                        <!-- <p class="mb-0 ml-2">Plus:</p> -->
                        <p class="mb-0">
                          {{
                            $t("users.dealersAddEdit.anySeanappsEquippedBoat")
                          }}
                        </p>
                        <p class="mb-0">
                          {{
                            $t("users.dealersAddEdit.customMaintenancePlans")
                          }}
                        </p>
                        <!-- <p class="mb-0">• Virtual QR Generate</p> -->
                        <p class="mb-0">
                          {{
                            $t(
                              "users.dealersAddEdit.taskAllocationToExternalServicePartner"
                            )
                          }}
                        </p>
                        <!-- <p class="mb-0 mt-2">Upcoming:</p> -->
                        <!-- <p class="mb-0">• Custom alerts</p> -->
                        <!-- <p class="mb-0">• Channel to sell services</p> -->
                      </v-card-text>
                    </v-col>
                    <v-col cols="6">
                      <v-card-text class="pt-0 pb-2 font-weight-bold">
                        {{ $t("users.dealersAddEdit.multiLocation") }}
                      </v-card-text>
                      <!-- <v-card-text class="pt-0">
                        +30€ per month on top of your chosen plan
                      </v-card-text> -->

                      <v-tooltip bottom :disabled="hasMultibaseLicence">
                        <template v-slot:activator="{ on, attrs }">
                          <v-chip
                            v-bind="attrs"
                            v-on="on"
                            class="mb-4 ml-2 pt-0"
                            color="primary"
                            outlined
                            :disabled="!hasMultibaseLicence"
                          >
                            {{
                              hasMultibaseLicence
                                ? $t("users.dealersAddEdit.active")
                                : $t("users.dealersAddEdit.inactive")
                            }}
                          </v-chip>
                        </template>
                        <span
                          v-html="
                            $t(
                              'users.dealersAddEdit.toUpgradeYourLicencePleaseContact',
                              {
                                email: 'support@seanapps.fr',
                              }
                            )
                          "
                        ></span>
                      </v-tooltip>

                      <v-card-text class="pt-0">
                        <p class="mb-2">
                          {{ $t("users.dealersAddEdit.bulletAffiliates") }}
                        </p>

                        {{
                          $t(
                            "users.dealersAddEdit.multiLocationPlanIsAutomaticallyAppliedIfYourPartnerHasMultipleServicePoints"
                          )
                        }}
                      </v-card-text>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12">
                      <v-card-text
                        class="pt-0 pb-1"
                        v-html="
                          $t(
                            'users.dealersAddEdit.toUpgradeYourLicencePleaseContact',
                            { email: getLinks.supportEmail }
                          )
                        "
                      >
                      </v-card-text>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-tab-item>
            <v-tab-item value="users">
              <v-card max-width="650px" width="650px" v-if="!loading">
                <v-card-text>
                  <v-row>
                    <v-col cols="12" v-if="editedItem.users.length !== 0">
                      <v-card-subtitle class="mb-0 pb-0">{{
                        $t("users.dealersAddEdit.addedUsers")
                      }}</v-card-subtitle>
                      <v-list
                        v-for="(user, index) in editedItem.users"
                        :key="user.uuid"
                      >
                        <user-email-component
                          :user="user"
                          :contact-email="editedItem.email"
                          :readonly="isInternalPartner"
                          :is-owner="isCurrentUserOwenerOfPartnership"
                          @delete-user="deleteUserDialog(index, user)"
                          @update-role="handleUpdateRole"
                        >
                          <template
                            slot="role"
                            v-if="
                              typeof user.labels !== 'undefined' &&
                              user.labels.length !== 0
                            "
                          >
                            <v-chip
                              outlined
                              class="mt-2 mr-1"
                              v-for="item in getRolePrettified(user.labels)"
                              :key="item.id"
                            >
                              {{ item.name }}
                            </v-chip>
                          </template>
                        </user-email-component>
                      </v-list>
                    </v-col>
                    <v-col
                      cols="12"
                      class="pb-0 mb-0"
                      v-if="!isInternalPartner"
                    >
                      <v-card-subtitle class="mb-0 pb-0">{{
                        $t("users.dealersAddEdit.inviteUsers")
                      }}</v-card-subtitle>
                      <user-email-invite
                        class="mx-6 ml-4"
                        :initial_data="editedItem.pending_invites"
                        :notAvailable="editedItem.users"
                        @invite-user="inviteUser"
                        @reject-user="rejectUser"
                        :contact-email="editedItem.email"
                        ref="userEmailInvite"
                      >
                        <template v-slot:locale>
                          <v-select
                            class="mt-0 pt-0"
                            color="primary"
                            v-model="inviteUserLanguage"
                            :items="userLanguageOptions"
                            item-value="id"
                            item-text="title"
                          >
                          </v-select>
                        </template>
                        <template v-slot:button>
                          <v-btn
                            primary
                            text
                            class="float-right pr-0 pl-0"
                            @click="handleAddUser"
                          >
                            Invite User
                          </v-btn>
                        </template>
                      </user-email-invite>
                    </v-col>
                    <!-- <v-col
                      cols="12"
                      class="mt-0 pt-0"
                      v-if="!isInternalPartner"
                    >
                      <v-btn
                        primary
                        text
                        class="float-right mr-2"
                        @click="handleAddUser"
                      >
                        Invite User
                      </v-btn>
                    </v-col> -->
                  </v-row>
                </v-card-text>
                <!-- <v-card-actions>
                  <v-spacer />
                  <v-btn color="primary" text @click="close"
                    >Cancel</v-btn
                  >
                  <v-btn color="primary" text @click="save"
                    >Save</v-btn
                  >
                </v-card-actions> -->
              </v-card>
            </v-tab-item>

            <v-tab-item value="affiliates">
              <v-card max-width="650px" width="650px" v-if="!loading">
                <v-card-text>
                  <v-row>
                    <v-col cols="12">
                      <v-card-actions class="pl-0">
                        <v-card-subtitle
                          >{{ $t("users.dealersAddEdit.affiliates") }}
                        </v-card-subtitle>
                        <v-spacer></v-spacer>
                        <v-btn
                          @click="openlinkedPartnerAddEditDialog"
                          color="primary"
                          class="mr-2"
                          text
                          v-if="editedItem.affiliates_permission === 'WRITE'"
                          >{{ $t("users.dealersAddEdit.addNew") }}</v-btn
                        >
                      </v-card-actions>
                      <v-list
                        v-if="editedItem.sub_partners.length === 0"
                        class="pt-0 pb-0"
                      >
                        <v-list-item>
                          <v-list-item-content>
                            <v-list-item-subtitle>
                              {{
                                $t(
                                  "users.dealersAddEdit.noAffiliatePartnersAvailable"
                                )
                              }}
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                      <v-list
                        v-else
                        class="pt-0 pb-0"
                        v-for="(partner, index) in editedItem.sub_partners"
                        :key="index"
                      >
                        <v-list-item>
                          <v-list-item-content>
                            <v-list-item-title>
                              <span>
                                {{ partner.name }}
                              </span>
                              <v-chip
                                class="ml-2"
                                v-if="
                                  typeof partner.partner_status !== 'undefined'
                                "
                              >
                                <span
                                  v-if="partner.partner_status === 'ACTIVE'"
                                  >{{ $t("users.dealersAddEdit.active") }}</span
                                ><span
                                  v-if="partner.partner_status === 'INACTIVE'"
                                  >{{ $t("users.dealersAddEdit.inactive") }}
                                </span>
                              </v-chip>
                            </v-list-item-title>
                          </v-list-item-content>
                          <v-card-actions class="pr-0">
                            <v-btn
                              icon
                              @click="handleEditLinkedPartners(partner)"
                              v-if="
                                editedItem.affiliates_permission === 'WRITE'
                              "
                            >
                              <v-icon color="primary">mdi-pencil</v-icon>
                            </v-btn>
                            <v-spacer></v-spacer>
                            <v-btn
                              icon
                              v-if="
                                editedItem.affiliates_permission === 'WRITE'
                              "
                              @click="handleRemoveLinkedPartners(partner.uuid)"
                            >
                              <v-icon color="red">mdi-delete</v-icon>
                            </v-btn>
                          </v-card-actions>
                        </v-list-item>
                      </v-list>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-tab-item>

            <v-tab-item v-if="isEditView" value="services">
              <v-card max-width="650px" width="650px" v-if="!loading">
                <v-card-text>
                  <partner-services
                    :services.sync="services"
                    :read-only="editedItem.services_permission"
                  ></partner-services>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-text class="pt-0">
                  <v-list flat subheader three-line class="mt-2">
                    <v-list-item>
                      <template>
                        <v-list-item-content>
                          <v-card-title class="subtitle-1 pl-0">
                            {{ $t("users.dealersAddEdit.privacyPolicy") }}
                          </v-card-title>
                          <v-card-subtitle class="pl-0 pb-1">
                            {{
                              $t(
                                "users.dealersAddEdit.whenAccessingCustomerSDataYouNeedToRespectAPrivacyPolicyWhichDefinesWhatYouCanAndCannotDoWithTheTelematicsDataClickButtonBellowToReviewTheContentOfAStandardPrivacyPolicyIfYouWishToProvideACustomPrivacyPolicyToYourCustomersPleaseContractSupportSentinelmarineNet",
                                { contactEmail: getLinks.supportEmail }
                              )
                            }}
                          </v-card-subtitle>

                          <v-card-subtitle class="pl-0 pt-0">
                            <v-btn
                              class="pl-0 pr-0"
                              x-small
                              color="secondary"
                              text
                              @click="handleLearnMore('pp')"
                            >
                              {{ $t("users.dealersAddEdit.privacyPolicy") }}
                            </v-btn>
                          </v-card-subtitle>
                        </v-list-item-content>
                      </template>
                    </v-list-item>
                    <v-list-item>
                      <template>
                        <v-list-item-content>
                          <v-card-title class="subtitle-1 pl-0">
                            {{ $t("users.dealersAddEdit.termsAndConditions") }}
                          </v-card-title>
                          <v-card-subtitle class="pl-0 pb-1">
                            {{
                              $t(
                                "users.dealersAddEdit.clickButtonBelowToReviewTheContentOfTermsAndConditionsForPartners"
                              )
                            }}
                          </v-card-subtitle>

                          <v-card-subtitle class="pl-0 pt-0">
                            <v-btn
                              x-small
                              class="pl-0 ml-0 pr-0"
                              color="secondary"
                              text
                              @click="handleLearnMore('tac')"
                            >
                              {{
                                $t("users.dealersAddEdit.termsAndConditions")
                              }}
                            </v-btn>
                          </v-card-subtitle>
                        </v-list-item-content>
                      </template>
                    </v-list-item>
                  </v-list>
                </v-card-text>

                <v-card-text class="pt-0" v-if="canSaveServices">
                  <v-list flat subheader two-line>
                    <v-list-item>
                      <template>
                        <v-list-item-action>
                          <v-checkbox
                            color="primary"
                            v-model="editedItem.enableSaveBtn"
                            @change="handlePolicy"
                          ></v-checkbox>
                        </v-list-item-action>

                        <v-list-item-content class="font-weight-bold">
                          {{
                            $t(
                              "users.dealersAddEdit.yourCustomersWillBeNotifiedUponAvailabilityOfNewServicesDoNotProceedIfYouDonTHaveTheAuthorityToMakeSuchChanges"
                            )
                          }}
                        </v-list-item-content>
                      </template>
                    </v-list-item>
                  </v-list>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn v-if="!hideCancel" color="primary" text @click="close"
                    >{{ $t("users.dealersAddEdit.cancel") }}
                  </v-btn>
                  <v-btn
                    :dark="editedItem.enableSaveBtn"
                    color="primary"
                    :disabled="!editedItem.enableSaveBtn"
                    @click="saveServices"
                    v-if="canSaveServices"
                    >{{ $t("users.dealersAddEdit.save") }}</v-btn
                  >
                </v-card-actions>
              </v-card>
            </v-tab-item>
          </v-tabs-items>
        </v-form>
      </v-layout>
      <warning-dialog
        :dialog="changeAdminDialog.active"
        :text="changeAdminDialog.text"
        :title="changeAdminDialog.title"
        @agree="handleAdminRoleChange"
        :confirm="$t('users.dealersAddEdit.continue')"
        :reject="$t('users.dealersAddEdit.cancel')"
        v-show="false"
      />
      <warning-dialog
        :dialog="cancelDialog.active"
        :text="cancelDialog.text"
        :title="cancelDialog.title"
        @agree="handleCancelDialogOutput"
        :confirm="$t('users.dealersAddEdit.yes')"
        :reject="$t('users.dealersAddEdit.no')"
        v-show="false"
      />
      <warning-dialog
        :dialog="deleteAdminDialog.active"
        :text="deleteAdminDialog.text"
        :title="deleteAdminDialog.title"
        @agree="handleCancelDialogOutput"
        :confirm="$t('users.dealersAddEdit.continue')"
        :reject="$t('users.dealersAddEdit.cancel')"
        v-show="false"
      />
      <warning-dialog
        :dialog="createInitialUserDialog.active"
        :text="createInitialUserDialog.text"
        :title="createInitialUserDialog.title"
        @agree="save"
        :confirm="$t('users.dealersAddEdit.yes')"
        :reject="$t('users.dealersAddEdit.no')"
        v-show="false"
      />
      <warning-dialog
        :dialog="removeLinkedPartner.active"
        :text="removeLinkedPartner.text"
        :title="removeLinkedPartner.title"
        @agree="handleRemoveLinkedPartnerResult"
        :confirm="$t('users.dealersAddEdit.yes')"
        :reject="$t('users.dealersAddEdit.no')"
        v-show="false"
      />
      <help-dialog
        :dialog="helpDialog"
        @close="closeHelpDialog"
        v-if="helpDialog.active"
      ></help-dialog>
      <linked-partner-add-edit-dialog
        v-if="linkedPartnerAddEditDialog.active"
        :dialog="linkedPartnerAddEditDialog"
        @save="handleLinkedPartnerAddEditDialogSave"
        @update="handleLinkedPartnerAddEditDialogUpdate"
        @close="closeLinkedPartnerAddEditDialog"
      ></linked-partner-add-edit-dialog>
      <information-dialog
        v-if="learnMoreDialog.active"
        :dialog="learnMoreDialog"
        @agree="handleLearnMoreDialog"
      ></information-dialog>

      <warning-dialog
        :dialog="existingRoleDialog.active"
        :text="existingRoleDialog.text"
        :title="existingRoleDialog.title"
        @agree="handleRoleChange"
        :confirm="$t('users.dealersAddEdit.continue')"
        :reject="$t('users.dealersAddEdit.cancel')"
        v-show="false"
      />
    </v-container>
  </v-content>
</template>

<script>
import api from "@/utils/api.js";
import WarningDialog from "@/components/dialogs/WarningDialog.vue";
import InformationDialog from "@/components/dialogs/InformationDialog.vue";
import UserEmailInvite from "@/components/miscellaneous/UserEmailInvite";
import snackbar from "@/utils/snackbar.js";
import UserEmailComponent from "@/components/miscellaneous/UserEmailComponent";
import BrandSelector from "@/components/selectors/BrandSelector";
import HelpDialog from "@/components/dialogs/HelpDialog";
import constraints from "@/utils/constraints";
import restrictions from "@/utils/restrictions";
import PartnerServices from "@/components/miscellaneous/PartnerServices.vue";
import { mapGetters } from "vuex";
import CountrySelector from "@/components/selectors/CountrySelector.vue";
import LinkedPartnerAddEditDialog from "@/components/dialogs/LinkedPartnerAddEditDialog.vue";
import constants from "@/utils/constants";
import { USER_CRM_REQUEST } from "@/store/actions/user";
import cookie from "@/utils/cookie";

export default {
  components: {
    UserEmailInvite,
    UserEmailComponent,
    WarningDialog,
    BrandSelector,
    HelpDialog,
    PartnerServices,
    CountrySelector,
    LinkedPartnerAddEditDialog,
    InformationDialog,
  },
  data: () => ({
    helpDialog: { location: null, active: false },
    valid: true,
    // dealerNameRule: [(v) => !!v || "Company display name is required"],
    // companyRule: [(v) => !!v || "Company legal name is required"],
    // emailRules: [(v) => /.+@.+\..+/.test(v) || "E-mail must be valid"],
    userLanguageOptions: [],
    inviteUserLanguage: null,
    dealerNameRule: [],
    companyRule: [],
    emailRules: [],
    loading: false,
    isEditView: false,
    services: [],
    editedItem: {
      name: "",
      brand_uuids: [],
      crm_group_uuid: "",
      brands: [],
      dealer: [],
      vendor: null,
      domains: [],
      address: "",
      city: "",
      country: "",
      email: "",
      website: "",
      phone: "",
      users: [],
      user_emails: [],
      enableSaveBtn: false,
      pending_invites: [],
      sub_partners: [],
      supergroup_uuid: null,
      readonly_fields: [],
      licence: { sku: "basic", name: "Basic" },
    },
    existingRoleDialog: {
      active: false,
      roles: [],
      user: {},
      title: "",
      text: "",
    },
    loadingUsers: false,
    indexToDelete: null,
    cancelDialog: {
      active: false,
      title: "",
      text: "",
    },
    deleteAdminDialog: {
      active: false,
      title: "",
      text: "",
    },
    changeAdminDialog: {
      active: false,
      roles: [],
      user: {},
      title: "",
      text: "",
    },
    createInitialUserDialog: {
      active: false,
      title: "",
      text: "",
    },
    tabs: [],
    removeLinkedPartner: {
      active: false,
      title: "",
      text: "",
      uuid: null,
    },
    linkedPartnerAddEditDialog: { active: false, editedItem: {} },
    learnMoreDialog: { active: false },
  }),

  computed: {
    ...mapGetters(["getMe", "getLinks", "getTheme"]),

    hasMultibaseLicence() {
      if (this.editedItem.affiliates_permission !== "NONE" && this.isEditView) {
        return true;
      }
      return false;
    },
    getLicence() {
      return this.editedItem.licence;
    },
    canEditServices() {
      let dealership = this.getMe.dealerships.find((dealership) => {
        return dealership.uuid === this.$route.params.id;
      });

      if (typeof dealership === "undefined") {
        return false;
      }

      return dealership.has_onboarding_feature;
    },
    canSaveServices() {
      let readOnlyServices = this.services.filter((item) => {
        return !item.is_read_only;
      });

      if (readOnlyServices.length === 0) {
        return false;
      }

      return true;
    },

    isInternalPartner() {
      if (
        typeof this.$route.query.my_account !== "undefined" &&
        this.$route.query.my_account &&
        this.isEditView
      ) {
        if (typeof this.editedItem.is_internal_partner === "undefined") {
          return false;
        }

        if (this.editedItem.is_internal_partner) {
          return true;
        }
      }
      return false;
    },
    canUseAffiliatedPartners() {
      return !!restrictions.canUseCrmFeature("can_use_affiliated_partners");
    },

    isSuperGroupPartner() {
      if (
        typeof this.$route.query.my_account !== "undefined" &&
        this.$route.query.my_account &&
        this.isEditView
      ) {
        if (
          typeof this.editedItem.is_internal_partner === "undefined" ||
          typeof this.editedItem.partner_group.is_supergroup === "undefined"
        ) {
          return false;
        }

        if (
          this.editedItem.is_internal_partner &&
          this.editedItem.partner_group.is_supergroup
        ) {
          return true;
        }
      }

      return false;
    },

    hideCancel() {
      return (
        this.$route.query.hasOwnProperty("single") && this.$route.query.single
      );
    },

    tab: {
      set(tab) {
        this.$router.replace({ query: { ...this.$route.query, tab } });
      },
      get() {
        return this.$route.query.tab;
      },
    },

    isCurrentUserOwenerOfPartnership() {
      if (typeof this.getMe.user === "undefined") return false;

      const checkForPermissionsOfCurrentUser = this.editedItem.users.find(
        (item) => {
          return this.getMe.user.username === item.username;
        }
      );

      if (
        typeof checkForPermissionsOfCurrentUser === "undefined" ||
        typeof checkForPermissionsOfCurrentUser.labels === "undefined"
      ) {
        console.error(
          "Invalid user or labels not found",
          this.getMe.user.username
        );
        return false;
      }

      // this.getCanUserWritePartners the creator can also reassign owner of the partner
      return (
        checkForPermissionsOfCurrentUser.labels.includes("ADMIN") ||
        restrictions.canUserWritePartnersPermission(
          this.getMe,
          this.editedItem.uuid
        )
      );
      // canUseCrmGroupFeature
    },
  },

  created() {
    this.setInitialData();
  },

  methods: {
    setInitialData(uuid = null) {
      //TODO:REFACTOR stings below

      this.userLanguageOptions = [
        {
          id: "en",
          title: this.$t(
            "components.selectors.multilingualDocumentSelector.english"
          ),
        },
        {
          id: "de",
          title: this.$t(
            "components.selectors.multilingualDocumentSelector.german"
          ),
        },
        {
          id: "fr",
          title: this.$t(
            "components.selectors.multilingualDocumentSelector.french"
          ),
        },
        {
          id: "it",
          title: this.$t(
            "components.selectors.multilingualDocumentSelector.italian"
          ),
        },
        {
          id: "es",
          title: this.$t(
            "components.selectors.multilingualDocumentSelector.spanish"
          ),
        },
      ];

      this.handleUserLanguageSelection();

      this.dealerNameRule = [
        (v) => !!v || this.$t("users.dealersAddEdit.partnerNameIsRequired"),
      ];

      this.companyRule = [
        (v) => !!v || this.$t("users.dealersAddEdit.companyNameIsRequired"),
      ];

      this.emailRules = [
        (v) =>
          /.+@.+\..+/.test(v) ||
          this.$t("users.dealersAddEdit.eMailMustBeValid"),
      ];

      this.cancelDialog = {
        active: false,
        title: this.$t("users.dealersAddEdit.removeUser"),
        text: this.$t("users.dealersAddEdit.areYouSureYouWantToRemoveThisUser"),
      };
      this.deleteAdminDialog = {
        active: false,
        title: this.$t(
          "users.dealersAddEdit.areYouSureYouWishToRemoveTheCurrentAdminUser"
        ),
        text: this.$t(
          "users.dealersAddEdit.warningConsiderTransferringTheAdminLabelToANewUserBeforeRemovingTheAdminUserOtherwiseBoatsWithAppStatusPartnerWillReceiveAppStatusCustomerAndWillNoLOngerBeAvailableToOtherPartnerUsersInTheEndUserApps"
        ),
      };
      this.changeAdminDialog = {
        active: false,
        roles: [],
        user: {},
        dealerName: "",
        title: this.$t(
          "users.dealersAddEdit.areYouSureYouWishToChangeTheCurrentAdminUser"
        ),
        text: this.$t(
          "users.dealersAddEdit.warningChangingTheuserHoldingTheAdminLabelWillCancelAnyPendingBoatTransfersForAllBoatsCurrentlyHeldByYouMayNeedToReInitiateTheBoatTransfersFollowingThisChange",
          { dealerName: this.getDealerName }
        ),
      };
      this.createInitialUserDialog = {
        active: false,
        title: this.$t("users.dealersAddEdit.addUser"),
        text: "",
      };
      this.removeLinkedPartner = {
        active: false,
        title: this.$t("users.dealersAddEdit.removeAffiliatePartner"),
        text: this.$t(
          "users.dealersAddEdit.areYouSureYouWantToRemoveThisAffiliatedPartner"
        ),
        uuid: null,
      };

      this.existingRoleDialog = {
        active: false,
        roles: [],
        user: {},
        title: this.$t("users.dealersAddEdit.warning"),
        text: this.$t(
          "users.dealersAddEdit.theNewUserWillBecomeTheOwnerOfThisAccountYourAccountWillLooseAccessToFeaturesAvailableOnlyToOwnersTHISCANNOTBEUNDONE"
        ),
      };

      return new Promise((resolve, reject) => {
        this.canUseKnowledgeBaseBtn =
          constraints.canUseKnowledgeBaseBtn("account");

        try {
          this.editedItem.supergroup_uuid = this.getMe.crm_groups.find(
            (item) => {
              return item.is_supergroup;
            }
          ).uuid;
        } catch (error) {
          this.editedItem.supergroup_uuid = null;
        }

        if (
          this.$route.name !== "dealerEdit" &&
          this.$route.name !== "partnerEdit"
        ) {
          this.setTabs();
          resolve();
          return;
        }

        const paramUuid = uuid === null ? this.$route.params.id : uuid;

        this.loading = true;
        this.isEditView = true;

        //NOTE: Search and page removed from query params because they are not needed for the api call
        const { search, page, ...queryParams } = this.$route.query;
        api.getDealer(paramUuid, queryParams).then((resp) => {
          this.editedItem = resp.data;
          this.editedItem.crm_group_uuid = this.editedItem.crm_group.uuid;
          this.editedItem.brand_uuids = this.editedItem.brands.map(
            (brand) => brand.uuid
          );
          this.setTabs();
          this.isSuperGroupPartner
            ? this.loadBrandServices(this.editedItem.partner_group.uuid)
            : this.loadPartnerServices();
          this.loading = false;
          resolve();
        });
      });
    },

    getTabs() {
      this.tabs = [
        {
          name: this.$t("users.dealersAddEdit.profile"),
          id: "information",
          icon: "mdi-information",
          href: "info",
          disabled: false,
        },
        {
          name: this.$t("users.dealersAddEdit.licence"),
          id: "subscription",
          icon: "mdi-star-outline",
          href: "subscription",
          disabled: false,
        },
        {
          name: this.$t("users.dealersAddEdit.users"),
          id: "users",
          icon: "mdi-cog",
          href: "users",
          disabled: false,
        },
        {
          name: this.$t("users.dealersAddEdit.affiliates"),
          id: "affiliates",
          icon: "mdi-account",
          href: "affiliates",
          disabled: false,
        },
        {
          name: this.$t("users.dealersAddEdit.services"),
          id: "services",
          icon: "mdi-account-cog-outline",
          href: "services",
          disabled: false,
        },
      ];
    },

    setTabs() {
      this.getTabs();
      const findTabIndex = (id, tabs) => {
        return tabs.findIndex((element) => {
          return element.id === id;
        });
      };

      if (!this.isEditView) {
        let index = findTabIndex("users", this.tabs);
        this.tabs[index].disabled = true;
        // this.tabs.splice(index, 1);
      }

      //NOTE: The array below shows the uuids of the crm_groups for beneteau supergroup and the uuid of group Haudi AG
      //  [
      //     "3533e1a84f1748b78f5568049f27a7a3",
      //     "71476dc49f6d47f7ab51645a8f0a785f",
      //   ]

      if (
        this.getTheme.id !== "beneteau" ||
        !this.isEditView ||
        [
          "3533e1a84f1748b78f5568049f27a7a3",
          "71476dc49f6d47f7ab51645a8f0a785f",
          "7cfec1f05e79439b9dc97ceaf34ae906",
        ].indexOf(this.editedItem.crm_group.uuid) === -1
      ) {
        let index = findTabIndex("subscription", this.tabs);
        this.tabs.splice(index, 1);
      }

      if (
        this.editedItem.affiliates_permission === "NONE" ||
        !this.isEditView
      ) {
        let index = findTabIndex("affiliates", this.tabs);
        this.tabs.splice(index, 1);
      }

      if (this.editedItem.services_permission === "NONE" || !this.isEditView) {
        let index = findTabIndex("services", this.tabs);
        this.tabs.splice(index, 1);
      }
    },

    handleUpdateRole(event) {
      if (event.roles.includes("ADMIN")) {
        this.changeAdminDialog = {
          ...this.changeAdminDialog,
          roles: event.roles,
          user: event.user,
          active: true,
          text: this.$t(
            "users.dealersAddEdit.warningChangingTheuserHoldingTheAdminLabelWillCancelAnyPendingBoatTransfersForAllBoatsCurrentlyHeldByYouMayNeedToReInitiateTheBoatTransfersFollowingThisChange",
            { dealerName: this.editedItem.name }
          ),
        };
        return;
      }

      if (event.roles.every((item) => item === "COMMUNICATION_MANAGER")) {
        this.existingRoleDialog = {
          ...this.existingRoleDialog,
          roles: event.roles,
          user: event.user,
          active: false,
        };
        this.handleRoleChange(true);
        return;
      }

      this.existingRoleDialog = {
        ...this.existingRoleDialog,
        roles: event.roles,
        user: event.user,
        active: true,
      };
    },

    handleAdminRoleChange(event) {
      api
        .setUserLabels(
          this.editedItem.uuid,
          this.changeAdminDialog.user.uuid,
          this.changeAdminDialog.roles
        )
        .then((value) => {
          this.loadingUsers = true;
          this.$store.dispatch(USER_CRM_REQUEST);

          api.getDealer(this.editedItem.uuid).then((resp) => {
            this.editedItem.users = resp.data.users;
            this.changeAdminDialog = {
              ...this.changeAdminDialog,
              active: false,
              roles: [],
              user: {},
            };
          });
        });
    },

    handleRoleChange(event) {
      if (!event) {
        this.existingRoleDialog = {
          ...this.existingRoleDialog,
          active: false,
          roles: [],
          user: {},
        };
        return;
      }
      api
        .setUserLabels(
          this.editedItem.uuid,
          this.existingRoleDialog.user.uuid,
          this.existingRoleDialog.roles
        )
        .then((value) => {
          this.loadingUsers = true;

          this.$store.dispatch(USER_CRM_REQUEST);

          api.getDealer(this.editedItem.uuid).then((resp) => {
            this.editedItem.users = resp.data.users;
            this.loadingUsers = false;

            this.existingRoleDialog = {
              ...this.existingRoleDialog,
              active: false,
              roles: [],
              user: {},
            };
          });
        });
    },

    setPreferredUserInviteLanguage(preferredInviteUserLanguage) {
      let language = this.userLanguageOptions.find(
        (item) => item.id === preferredInviteUserLanguage
      );

      cookie.setCookie(
        "preferred-invite-user-language",
        language == null ? "en" : language.id,
        9999
      );

      return language == null ? "en" : language.id;
    },
    getPreferredUserInviteLanguage() {
      const preferredInviteUserLanguage = cookie.getCookie(
        "preferred-invite-user-language"
      );

      let language = this.userLanguageOptions.find(
        (item) => item.id === preferredInviteUserLanguage
      );

      if (language != null) {
        return language.id;
      }

      language = this.userLanguageOptions.find((item) => item.id === "en");
      return language.id;
    },
    handleUserLanguageSelection() {
      this.inviteUserLanguage = this.getPreferredUserInviteLanguage();
    },
    getRolePrettified(roles) {
      const partnerRoles = constants.partnerRoles;

      return partnerRoles.filter((item) => {
        return roles.includes(item.server);
      });
    },

    handleRemoveLinkedPartners(uuid) {
      this.removeLinkedPartner = {
        ...this.removeLinkedPartner,
        uuid: uuid,
        active: true,
      };
    },
    handleRemoveLinkedPartnerResult(event) {
      if (event) {
        this.loading = true;
        api
          .deleteDealer(this.removeLinkedPartner.uuid)
          .then((resp) => {
            this.loadDealer();
          })
          .catch(() => {
            this.loading = false;
          });
      }
      this.removeLinkedPartner = {
        ...this.removeLinkedPartner,
        uuid: null,
        active: false,
      };
    },
    loadDealer() {
      return new Promise((resolve, reject) => {
        api.getDealer(this.$route.params.id).then((resp) => {
          this.editedItem = resp.data;
          this.editedItem.crm_group_uuid = this.editedItem.crm_group.uuid;
          this.editedItem.brand_uuids = this.editedItem.brands.map(
            (brand) => brand.uuid
          );
          this.loading = false;
          resolve();
        });
      });
    },
    handleEditLinkedPartners(partner) {
      this.$router.replace({
        name: "dealerEdit",
        params: {
          id: partner.uuid,
        },
        query: {
          ...this.$route.query,
          search: "",
          tab: "info",
          edit: "affiliate",
        },
      });
    },
    openlinkedPartnerAddEditDialog() {
      const { city, address, country, company_name } = this.editedItem;

      if (
        restrictions.canUseCrmFeatureCompareString(
          "can_use_affiliated_partners",
          "CHILD"
        )
      ) {
        this.linkedPartnerAddEditDialog = {
          active: true,
          editedItem: { city, address, country, company_name },
          readonlyFields: ["company_name"],
        };
        return;
      }

      if (
        restrictions.canUseCrmFeatureCompareString(
          "can_use_affiliated_partners",
          "INDIE"
        )
      ) {
        this.linkedPartnerAddEditDialog = {
          active: true,
          editedItem: { city, address, country, company_name },
          readonlyFields: [],
        };
        return;
      }

      snackbar.error();
      console.error(
        "can_use_affiliated_partners",
        restrictions.getCrmFeature("can_use_affiliated_partners")
      );
    },

    handleLinkedPartnerAddEditDialogSave(event) {
      this.create(event);
    },

    handleLinkedPartnerAddEditDialogUpdate(event) {
      this.update(event);
    },

    closeLinkedPartnerAddEditDialog() {
      this.linkedPartnerAddEditDialog = { active: false, editedItem: {} };
    },

    readonlyFields(input) {
      if (this.$route.name === "dealerNew") {
        return false;
      }

      if (this.editedItem.readonly_fields.length === 0) return false;

      return !!this.editedItem.readonly_fields.find(
        (element) => element === input
      );
    },

    handleLearnMore(event) {
      this.learnMoreDialog = {
        active: false,
        text: "",
        title: "",
        options: {},
      };

      if (event == "pp") {
        api.getPartnerPp(this.$route.params.id).then((resp) => {
          this.learnMoreDialog = {
            active: true,
            text: resp.data,
            title: this.$t("users.dealersAddEdit.privacyPolicy"),
            options: {
              persistent: false,
              width: 600,
              noTitle: true,
              html: true,
              disableReject: true,
              confirmText: this.$t("users.dealersAddEdit.close"),
              scrollable: true,
            },
          };
        });
      } else if (event == "tac") {
        api.getPartnerTac().then((resp) => {
          this.learnMoreDialog = {
            active: true,
            text: resp.data,
            title: this.$t("users.dealersAddEdit.termsAndConditions"),
            options: {
              persistent: false,
              width: 600,
              noTitle: true,
              html: true,
              disableReject: true,
              confirmText: this.$t("users.dealersAddEdit.close"),
              scrollable: true,
            },
          };
        });
      }
    },

    handleLearnMoreDialog() {
      this.learnMoreDialog = {
        active: false,
        text: "",
        title: "",
        options: {},
      };
    },
    handlePolicy() {},
    saveServices() {
      let data = this.services
        .filter((service) => service.enabled)
        .map((service) => service.uuid);

      let post = { enabled_services: data };
      this.isSuperGroupPartner
        ? this.setBrandServices(post)
        : this.setPartnerServices(post);
    },

    setPartnerServices(post) {
      api.setPartnerServices(this.$route.params.id, post).then((resp) => {
        this.services = [];
        snackbar.success(this.$t("utils.snackbar.success.successfullySaved"));
        resp.data.forEach((element) => {
          element.learnMore = false;
          this.$set(this.services, this.services.length, element);
          this.close();
        });
      });
    },

    setBrandServices(post) {
      api
        .setBrandServices(this.editedItem.partner_group.uuid, post)
        .then((resp) => {
          this.services = [];
          snackbar.success(this.$t("utils.snackbar.success.successfullySaved"));
          resp.data.forEach((element) => {
            element.learnMore = false;
            this.$set(this.services, this.services.length, element);
            this.close();
          });
        });
    },

    loadPartnerServices() {
      if (this.$route.params.id == null) {
        return;
      }

      api.getPartnerServices(this.$route.params.id).then((resp) => {
        resp.data.forEach((element) => {
          element.active = element.enabled !== null;
          element.learnMore = false;
          this.services.push(element);
        });
      });
    },

    loadBrandServices(uuid) {
      api.getBrandServices(uuid).then((resp) => {
        resp.data.forEach((element) => {
          element.active = element.enabled !== null;
          element.disabled = element.enabled;
          element.learnMore = false;
          this.services.push(element);
        });
      });
    },

    closeHelpDialog(event) {
      if (event === "close") {
        this.helpDialog.active = false;
      }
    },

    handleHelp(event) {
      this.helpDialog.active = true;
      this.helpDialog.location = "account";
    },
    handleAddUser() {
      this.$refs.userEmailInvite.addUser();
    },

    inviteUser(user, done) {
      api
        .inviteDealerUser(this.$route.params.id, {
          email: user,
          language: this.inviteUserLanguage || "en",
        })
        .then((resp) => {
          snackbar.success(
            this.$t("utils.snackbar.success.userSuccessfullyInvited")
          );

          this.setPreferredUserInviteLanguage(this.inviteUserLanguage);

          api.getDealer(this.$route.params.id).then((response) => {
            let findUser = response.data.users.find(
              (item) => item.username === resp.data.email
            );
            if (findUser) {
              this.editedItem.users.push(findUser);
              done(resp, true);
            } else {
              done(resp, false);
            }
          });
        })
        .catch((error) => {
          if (
            typeof error.response.data.error_type !== "undefined" &&
            error.response.data.error_type === "MEMBER_OF_OTHER_SUPERGROUPS"
          ) {
            snackbar.error(error.response.data.error);
          }
          this.loading = false;
        });
    },
    rejectUser(user, done) {
      api.rejectInviteDealerUser(user.token).then((reps) => {
        snackbar.success(this.$t("utils.snackbar.success.userWasRejected"));
        done(user);
      });
    },
    deleteUserDialog(index, user) {
      if (user.labels.includes("ADMIN")) {
        this.indexToDelete = index;
        this.deleteAdminDialog.active = true;
      } else {
        this.indexToDelete = index;
        this.cancelDialog.active = true;
      }
    },
    handleCancelDialogOutput(agreed) {
      this.loading = false;
      if (agreed) {
        api
          .deleteUserFromDealers(this.$route.params.id, {
            uuid: this.editedItem.users[this.indexToDelete].uuid,
          })
          .then((resp) => {
            this.editedItem.users.splice(this.indexToDelete, 1);
            snackbar.success(
              this.$t("utils.snackbar.success.userSuccessfullyRemoved")
            );
            this.indexToDelete = null;
          });
      } else {
        this.indexToDelete = null;
      }
      this.cancelDialog.active = false;
      this.deleteAdminDialog.active = false;
    },
    close() {
      this.$route.query.hasOwnProperty("tab") &&
        delete this.$route.query["tab"];

      if (
        this.$route.params.id === null ||
        this.$route.params.id === undefined
      ) {
        if (
          typeof this.$route.query.my_account !== "undefined" &&
          this.$route.query.my_account === "true"
        ) {
          this.$router.push({
            name: "partners",
            query: this.$route.query,
          });
        } else {
          this.$router.push({
            name: "dealers",
            query: this.$route.query,
          });
        }
      } else {
        if (
          typeof this.$route.query.my_account !== "undefined" &&
          this.$route.query.my_account === "true"
        ) {
          this.$router.push({
            name: "partnerDetails",
            id: this.$route.params.id,
            query: this.$route.query,
          });
        } else {
          this.$router.push({
            name: "dealerDetails",
            id: this.$route.params.id,
            query: this.$route.query,
          });
        }
      }
    },

    isUserAdded(user) {
      let pendingInvites = this.editedItem.pending_invites.map((user) => {
        return { ...user, username: user.email };
      });
      return (
        [...this.editedItem.users, ...pendingInvites].filter((item) => {
          return item.username === user;
        }).length === 0
      );
    },

    openCreateInitialUserDialog() {
      if (this.$refs.form.validate()) {
        if (
          this.editedItem.email !== "" &&
          this.editedItem.email !== null &&
          this.isUserAdded(this.editedItem.email) &&
          !this.isInternalPartner
        ) {
          this.createInitialUserDialog.text = this.$t(
            "users.dealersAddEdit.doYouWantToCreateUser",
            { email: this.editedItem.email }
          );
          this.createInitialUserDialog.active = true;
        } else {
          this.save();
        }
      } else {
        snackbar.error();
      }
    },

    // handleError(err) {
    //   if (err.status === 403) {
    //     snackbar.error("Don't have permission to edit dealers ");
    //   } else {
    //     snackbar.error("An error has occurred");
    //   }
    // },
    create(external = {}) {
      let data =
        Object.keys(external).length === 0 ? this.editedItem : external;
      api.createDealer(data).then((resp) => {
        this.$route.params.id = resp.data.uuid;
        snackbar.success(this.$t("utils.snackbar.success.successfullySaved"));
        if (Object.keys(external).length === 0) {
          this.close();
        } else {
          this.loading = true;
          this.loadDealer().then((resp) => {
            this.closeLinkedPartnerAddEditDialog();
            this.loading = false;
          });
        }
      });
    },
    update(external = {}) {
      let data =
        Object.keys(external).length === 0 ? this.editedItem : external;

      // if (typeof data.parent_partner.uuid !== "undefined") {
      //   data.parent_partner_uuid = data.parent_partner.uuid;
      // }

      api.updateDealer(data.uuid, data).then((resp) => {
        snackbar.success(this.$t("utils.snackbar.success.successfullySaved"));

        if (Object.keys(external).length === 0) {
          this.close();
        } else {
          this.loading = true;
          this.loadDealer().then((resp) => {
            this.closeLinkedPartnerAddEditDialog();
            this.loading = false;
          });
        }
      });
    },
    save(event) {
      if (typeof event !== "undefined" && event) {
        this.editedItem.create_initial_user = true;
      } else {
        this.editedItem.create_initial_user = false;
      }

      if (this.editedItem.parent_parent !== null) {
        this.editedItem.parent_partner_uuid = this.editedItem.parent_parent;
      }

      if (this.editedItem.brand_uuids.length > 0) {
        this.editedItem.crm_group_uuid = this.editedItem.brand_uuids[0];
      } else {
        this.editedItem.crm_group_uuid = this.editedItem.supergroup_uuid;
      }

      if (this.isEditView) {
        this.update();
      } else {
        this.create();
      }
    },
  },
  beforeRouteUpdate(to, from, next) {
    if (
      typeof to.query.edit !== "undefined" &&
      to.query.edit === "affiliate" &&
      to.name === "dealerEdit"
    ) {
      this.setInitialData(to.params.id).then(() => {
        const { edit, ...rest } = this.$route.query;
        this.$router.replace({
          name: "dealerEdit",
          query: { ...rest },
        });
        next();
      });
    }
    next();
  },
};
</script>
<style scoped></style>
